import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import defaultConfiguration from '../data/Configuration';
import type { Configuration } from '../data/Configuration';

export interface ConfigurationSliceState extends Configuration {}

const initialState: ConfigurationSliceState = defaultConfiguration;

export type UpdateConfigurationArgs = Partial<Configuration>;

const isValidVolume = (volume: number) => volume >= 0.0 && volume <= 1.0;

const configurationSlice = createSlice({
	name: 'highscore',
	initialState,
	reducers: {
		updateConfiguration: (
			state,
			action: PayloadAction<UpdateConfigurationArgs>
		): void => {
			const configuration = action.payload;
			if (configuration.backgroundSoundEnabled !== undefined) {
				state.backgroundSoundEnabled = configuration.backgroundSoundEnabled;
			}
			if (
				configuration.backgroundSoundVolume !== undefined &&
				isValidVolume(configuration.backgroundSoundVolume)
			) {
				state.backgroundSoundVolume = configuration.backgroundSoundVolume;
			}
			if (configuration.soundEffectsEnabled !== undefined) {
				state.soundEffectsEnabled = configuration.soundEffectsEnabled;
			}
			if (
				configuration.soundEffectsVolume !== undefined &&
				isValidVolume(configuration.soundEffectsVolume)
			) {
				state.soundEffectsVolume = configuration.soundEffectsVolume;
			}
			if (configuration.defaultPlayerName !== undefined) {
				state.defaultPlayerName = configuration.defaultPlayerName;
			}
			if (configuration.resetPlaythroughAfterIntro !== undefined) {
				state.resetPlaythroughAfterIntro =
					configuration.resetPlaythroughAfterIntro;
			}
			if (
				configuration.roundCountPerPlaythrough !== undefined &&
				configuration.roundCountPerPlaythrough > 0
			) {
				state.roundCountPerPlaythrough = configuration.roundCountPerPlaythrough;
			}
			if (
				configuration.initialPersonaCountPerPlaythrough !== undefined &&
				configuration.initialPersonaCountPerPlaythrough > 0
			) {
				state.initialPersonaCountPerPlaythrough =
					configuration.initialPersonaCountPerPlaythrough;
			}
			if (configuration.randomInitialPersonas !== undefined) {
				state.randomInitialPersonas = configuration.randomInitialPersonas;
			}
			if (
				configuration.additionalPersonaCountPerRound !== undefined &&
				configuration.additionalPersonaCountPerRound >= 0
			) {
				state.additionalPersonaCountPerRound =
					configuration.additionalPersonaCountPerRound;
			}
			if (configuration.randomAdditionalPersonas !== undefined) {
				state.randomAdditionalPersonas = configuration.randomAdditionalPersonas;
			}
			if (configuration.globalHighscoreEnabled !== undefined) {
				state.globalHighscoreEnabled = configuration.globalHighscoreEnabled;
			}
			if (
				configuration.maxHighscoreEntryCount !== undefined &&
				configuration.maxHighscoreEntryCount > 0
			) {
				state.maxHighscoreEntryCount = configuration.maxHighscoreEntryCount;
			}
			if (configuration.backendUrl !== undefined) {
				state.backendUrl = configuration.backendUrl;
			}
			if (configuration.surveyUrl !== undefined) {
				const url = configuration.surveyUrl;
				state.surveyUrl = !url ? undefined : url;
			}
			if (configuration.defaultCategory !== undefined) {
				state.defaultCategory = configuration.defaultCategory;
			}
		},
		resetConfiguration: () => initialState,
		setBackgroundSoundEnabled: (state, action: PayloadAction<boolean>) => {
			const enabled = action.payload;
			state.backgroundSoundEnabled = enabled;
		},
		setBackgroundSoundVolume: (state, action: PayloadAction<number>) => {
			const volume = action.payload;
			state.backgroundSoundVolume = volume;
		},
		setSoundEffectsEnabled: (state, action: PayloadAction<boolean>) => {
			const enabled = action.payload;
			state.soundEffectsEnabled = enabled;
		},
		setSoundEffectsVolume: (state, action: PayloadAction<number>) => {
			const volume = action.payload;
			state.soundEffectsVolume = volume;
		}
	}
});

export const {
	updateConfiguration,
	resetConfiguration,
	setBackgroundSoundEnabled,
	setBackgroundSoundVolume,
	setSoundEffectsEnabled,
	setSoundEffectsVolume
} = configurationSlice.actions;
export default configurationSlice.reducer;
